import { useMemo, useState } from "react";

import type { DispatchRecord, PlatformInfo } from "../api";
import { heldReason, publishedStatuses } from "../lib/platformStatus";
import { diagnose, isRetryable } from "../lib/diagnose";
import { formatInTz } from "../lib/tz";
import { PlatformIcon } from "./PlatformIcon";

/**
 * Activity log — a permanent, per-user history of every post that has gone out,
 * successful AND failed, separate from the calendar. Unlike the calendar (which
 * can hide/clear entries) and the notifications inbox (which is dismissible),
 * this log is never cleared: a failed post stays here forever, with a plain-
 * English diagnosis + a suggested fix. "Clearing" a notification or a calendar
 * entry never removes anything from this record.
 *
 * Reads straight off `history` (dispatch records) — no new endpoint.
 */
type Filter = "all" | "failed";

const OUTCOME_LABEL = { published: "Published", held: "Held", failed: "Failed" } as const;

export function Activity({
  history,
  platforms,
  onRepost,
  onRetry,
  timezone,
}: {
  history: DispatchRecord[];
  platforms: PlatformInfo[];
  /** IANA zone each record's timestamp is displayed in (browser default). */
  timezone: string;
  /** Re-post a failed record (reopens the composer with its draft, failed
   * platforms pre-selected). Shown only when the record has a stored draft. */
  onRepost?: (rec: DispatchRecord) => void;
  /** Retry a failed record's transient legs immediately (no composer). Shown
   * instead of Re-post when every failed leg is retryable as-is. */
  onRetry?: (rec: DispatchRecord) => void;
}) {
  const [filter, setFilter] = useState<Filter>("all");

  // Newest first, with each record's per-platform outcomes resolved once.
  const rows = useMemo(() => {
    return [...history]
      .sort((a, b) => b.at - a.at)
      .map((rec) => ({ rec, statuses: publishedStatuses(rec.results, platforms) }));
  }, [history, platforms]);

  const failedCount = rows.filter((r) => r.statuses.some((s) => s.outcome === "failed")).length;
  const shown = filter === "failed" ? rows.filter((r) => r.statuses.some((s) => s.outcome === "failed")) : rows;

  if (history.length === 0) {
    return <p className="muted small">Nothing here yet — your published posts and any failures will show up in this log.</p>;
  }

  return (
    <div className="act">
      <div className="act-filters" role="tablist" aria-label="Filter activity">
        <button
          type="button"
          className={`act-filter${filter === "all" ? " on" : ""}`}
          aria-pressed={filter === "all"}
          onClick={() => setFilter("all")}
        >
          All ({rows.length})
        </button>
        <button
          type="button"
          className={`act-filter${filter === "failed" ? " on" : ""}`}
          aria-pressed={filter === "failed"}
          onClick={() => setFilter("failed")}
        >
          Needs attention ({failedCount})
        </button>
      </div>

      {shown.length === 0 ? (
        <p className="muted small">No failed posts — everything published cleanly. 🎉</p>
      ) : (
        <ul className="act-list">
          {shown.map(({ rec, statuses }) => {
            const failedLegs = statuses.filter((s) => s.outcome === "failed");
            const hasDraft = rec.draft != null;
            // Every failed leg transient → offer an immediate Retry; otherwise a
            // Re-post that opens the composer so the creator can fix what's wrong.
            const allRetryable =
              failedLegs.length > 0 && failedLegs.every((s) => s.error != null && isRetryable(s.error));
            const showRetry = hasDraft && allRetryable && onRetry != null;
            const showRepost = hasDraft && failedLegs.length > 0 && !showRetry && onRepost != null;
            return (
            <li key={rec.id} className="card act-item">
              <div className="act-head">
                <span className="act-title">{rec.title || "Untitled post"}</span>
                <span className="act-head-right">
                  {showRetry && (
                    <button
                      type="button"
                      className="btn small"
                      onClick={() => onRetry(rec)}
                      title="Send this post's failed platforms again"
                    >
                      Retry
                    </button>
                  )}
                  {showRepost && (
                    <button
                      type="button"
                      className="btn small"
                      onClick={() => onRepost(rec)}
                      title="Reopen this post in the composer to fix and send it again"
                    >
                      Re-post →
                    </button>
                  )}
                  <span className="muted small">
                    {formatInTz(rec.at, timezone, { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                </span>
              </div>
              <ul className="act-legs">
                {statuses.map((s, i) => {
                  const info = platforms.find((p) => p.id === s.id);
                  return (
                    <li key={`${s.id}-${i}`} className={`act-leg act-leg--${s.outcome}`}>
                      <span className="act-leg-plat">
                        {info ? (
                          <PlatformIcon platform={info} size={18} />
                        ) : (
                          <span className="act-leg-dot" aria-hidden="true" />
                        )}
                        <span className="act-leg-name">{s.name}</span>
                      </span>
                      <span className={`act-leg-status act-leg-status--${s.outcome}`}>
                        {OUTCOME_LABEL[s.outcome]}
                      </span>
                      {s.outcome === "published" && s.url && (
                        <a className="act-leg-link" href={s.url} target="_blank" rel="noreferrer">
                          View post ↗
                        </a>
                      )}
                      {s.outcome === "held" && (
                        <span className="muted small act-leg-note">{heldReason(s.id)}</span>
                      )}
                      {s.outcome === "failed" && s.error && (
                        <div className="act-leg-fix">
                          {/* Plain-English "what happened" — not the raw provider
                              blob — matching the calendar's day-detail. The raw
                              error stays available under a collapsed details. */}
                          <div className="act-leg-diag">{diagnose(s.error).diagnosis}</div>
                          <div className="act-fix">
                            <span className="act-fix-k">What to do</span>
                            {diagnose(s.error).action}
                          </div>
                          <details className="act-leg-raw">
                            <summary>Technical detail</summary>
                            <div className="act-leg-err">{s.error}</div>
                          </details>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
